import {E_GreenSocket_MessageTypes, E_GreenSocket_UnsubscribeReason} from "../../models/constants/GreenSocketConstants";
import {M_GreenSocketMessage} from "../../models/Models_GreenSocket";
import {Logger} from "../Logger";
import {ArrayUtils} from "../ArrayUtils";
import {uID} from "../../functions/generic";

const logger = new Logger("GreenSocketConnection");

export class GreenSocketConnection {
	type = "public";

	/**
	 * @type {GreenSocketV2}
	 */
	#controller;

	#listeners = [];

	/**
	 * Currently active subscriptions (this will be used when the connection is reestablished)
	 */
	#subscriptions = [];

	/**
	 * Queue of commands that are to be sent after a connection is reestablished
	 */
	#queue = [];

	constructor(controller, connectionType) {
		this.#controller = controller;
		this.type = connectionType;
	}

	/**
	 * Get socket
	 * ---
	 * @return {WebSocket}
	 */
	get socket() {
		return this.#controller.socket;
	}

	/**
	 * Is connected
	 * ---
	 * @return {boolean}
	 */
	get isConnected() {
		return this.#controller.isConnected;
	}

	/**
	 * Is authorized
	 * ---
	 * @return {boolean}
	 */
	get isAuthorized() {
		return this.#controller.isAuthorized;
	}

	/**
	 * Console identifier
	 * ---
	 * @return {string}
	 */
	get consoleIdentifier() {
		return `[${this.type}]`;
	}

	/**
	 * Resubscribe
	 * ---
	 * Resubscribes all topics. This is usually called when a connection is restored or the client is authorized and the "resubscribeAfterReconnect" in the options is truthy.
	 * @see GreenSocketV2._completeAuthorizationAfterConnect
	 */
	resubscribe() {
		if(!this.#subscriptions.length) return;

		let subscriptions = [...this.#subscriptions];
		this.#subscriptions = [];

		logger.isTypeAllowed("info") && console.info(`♻️ ${this.consoleIdentifier} Resubscribing...\n`, subscriptions);

		subscriptions.forEach(subscription => {
			if(subscription.resubscribe) {
				this.subscribeTopic(subscription.topic, subscription.unsubscribeCallback, true);
			}
			else {
				subscription.unsubscribeCallback(E_GreenSocket_UnsubscribeReason.NOT_RESUBSCRIBED);
			}
		});
	}

	/**
	 * Submit queue
	 * ---
	 * Submits a pending queue of messages. This is usually called when a connection is restored or the client is authorized.
	 * @see GreenSocketV2._completeAuthorizationAfterConnect
	 */
	submitQueue() {
		if(!this.#queue.length) return;

		let queue = [...this.#queue];
		this.#queue = [];

		logger.isTypeAllowed("info") && console.info(`⛓️ ${this.consoleIdentifier} Submitting queue...\n`, queue);

		queue.forEach(queue => this._sendMessage(queue));
	}

	/**
	 * On event
	 * ---
	 * Add a listener to a specified event, type and destination based on the arguments provided.
	 * @param {"connected"|"authorized"|"disconnected"|"message"|"error"|string} eventName Name of the event or a destination to listen to. If the value is not one of the predetermined, it's considered a destination.
	 * @param {function(payload: Object|Event): void} listener
	 * @param {E_GreenSocket_MessageTypes} type Optional type to match
	 * @param {boolean} replacePrevious Whether to replace any previous "on" listeners based on eventName, destination and type.
	 */
	on(eventName, listener, {type = undefined, replacePrevious = false} = {}) {
		let destination;

		if(["connected", "connect"].includes(eventName)) {
			eventName = "open";
		}
		else if(["disconnected", "disconnect"].includes(eventName)) {
			eventName = "close";
		}
		//"authorized" and "error" are custom events
		else if(!["open", "close", "message", "authorized", "error"].includes(eventName)) {
			destination = eventName;
			eventName = "message";
		}

		const identifier = `"${eventName}"${type ? ` typeof ${type}` : ''}${destination ? ` with destination "${destination}"` : ''}`;
		const id = uID();

		logger.isTypeAllowed("info") && console.info(`👂 ${this.consoleIdentifier} Adding listener for ${identifier}. ID:`, id);

		const remove = () => {
			ArrayUtils.removeID(this.#listeners, id, false);

			logger.isTypeAllowed("info") && console.info(`👂 ${this.consoleIdentifier} Listener for ${identifier} was removed. ID:`, id);
		};

		if(replacePrevious) {
			this.#listeners = this.#listeners.filter(listener => !(listener.eventName == eventName && listener.destination == destination && listener.type == type));
		}

		this.#listeners.push({
			id,
			type,
			eventName,
			listener,
			destination,
			remove,
		});

		return remove;
	}

	/**
	 * Subscribe topic
	 * ---
	 * @param {string} topic
	 * @param {function(reason: null|*): void} unsubscribeCallback
	 * @param {boolean} resubscribe
	 */
	subscribeTopic(topic, unsubscribeCallback = () => null, resubscribe = true) {
		logger.isTypeAllowed("info") && console.info(`💬 ${this.consoleIdentifier} Subscribing to topic "${topic}"...`);

		if(this.#subscriptions.some(subscription => subscription.topic == topic)) {
			logger.isTypeAllowed("warning") && console.warn(`💬 ${this.consoleIdentifier} Topic "${topic}" is already subscribed. This request to subscribe is ignored.`);

			return;
		}

		this._subscribeTopic(topic);
		this.#subscriptions.push({
			topic,
			unsubscribeCallback,
			resubscribe,
		});
	}

	/**
	 * Unsubscribe topic
	 * ---
	 * @param {string} topic
	 * @param {*} reason Internal reason for the unsubscription.
	 * @param {string} sentReason A reason that will be sent to the backend along the unsubscribe request.
	 */
	unsubscribeTopic(topic, reason = null, sentReason = undefined) {
		logger.isTypeAllowed("info") && console.info(`💬 ${this.consoleIdentifier} Unsubscribing "${topic}"...\nReason: ${reason}\nSent reason: ${sentReason}`);

		const removedSuccessfully = this._removeTopicFromSubscriptions(topic, reason);

		if(!removedSuccessfully) {
			logger.isTypeAllowed("warning") && console.warn(`💬 ${this.consoleIdentifier} Topic "${topic}" is not subscribed. This request to unsubscribe is ignored.`);

			return;
		}

		this._unsubscribeTopic(topic, sentReason);
	}

	/**
	 * Send message
	 * ---
	 * Sends a message.
	 * @param {E_GreenSocket_MessageTypes} type Message type.
	 * @param {*} data
	 * @param {string} destination
	 * @param {string} id Custom ID
	 * @param {boolean} pushToQueueOnFail Whether to push the message to the queue if it fails to sent for any reason. (e.g. connection is not established or authorized)
	 */
	sendMessage(type, data, destination = undefined, {id = undefined, pushToQueueOnFail = true} = {}) {
		let message = {
			...M_GreenSocketMessage,
			id,
			type,
			destination,
			data,
		};

		return this._sendMessage(message, pushToQueueOnFail);
	}

	/**
	 * Process connected
	 * ---
	 * Processes the "open" event of the socket instance.
	 * @param {Event} e Open event
	 * @param {GreenSocketConnection} privateConnection Private socket connection instance to pass through so it can be used as an argument.
	 */
	processConnected(e, privateConnection = undefined) {
		this._notifyListeners("open", e, undefined, privateConnection);
	}

	/**
	 * Process disconnected
	 * ---
	 * Processes the "close" event of the socket instance.
	 * @param {Event} e Close event
	 * @param {boolean} isManualDisconnect Whether this connection was initiated manually by the client or due to some unforeseen circumstances like a connection loss.
	 * @param {string|*} reason Reason for termination.
	 */
	processDisconnected(e, isManualDisconnect, reason) {
		this._notifyListeners("close", e, undefined, isManualDisconnect ? reason : undefined);
	}

	/**
	 * Process message
	 * ---
	 * Processes the "message" event of the socket instance.
	 * @param {Object} payload Message payload (JSON parsed e.data)
	 * @param {Event} e Message event
	 * @param {GreenSocketConnection} privateConnectionInstance Private socket connection instance to pass through so it can be used as an argument.
	 */
	processMessage(payload, e, privateConnectionInstance = undefined) {
		this._notifyListeners("message", e, payload.destination, payload);

		if(payload.type == E_GreenSocket_MessageTypes.LOGIN) {
			this._notifyListeners("authorized", e, undefined, privateConnectionInstance);
		}
		else if(payload.type == "ERROR") {
			this._notifyListeners("error", e, payload.destination, payload);

			if(payload.destination && payload.data.code == "ACCESS_DENIED") {
				this._removeTopicFromSubscriptions(payload.destination, payload.code);
			}
		}
	}

	/**
	 * Notify listeners
	 * ---
	 * Notifies all relevant listeners
	 * @param {string} eventName
	 * @param {Event} e
	 * @param {string} destination
	 * @param {Object} data
	 * @private
	 */
	_notifyListeners(eventName, e, destination, data = e.data) {
		try {
			data = JSON.parse(data);
		} catch (e) {}

		this.#listeners.forEach(listener => {
			if(
				listener.eventName == eventName &&
				[listener.destination, '*'].includes(destination) &&
				(listener.type ? data?.type == listener.type : true)
			) {
				listener.listener(data, e);
			}
		});
	}

	/**
	 * @private
	 * Subscribe topic
	 * ---
	 * Sends subscribe message
	 * @param {string} topic
	 */
	_subscribeTopic(topic) {
		if(this.sendMessage(E_GreenSocket_MessageTypes.SUBSCRIBE, undefined, topic, {pushToQueueOnFail: false})) {
			logger.isTypeAllowed("info") && console.info(`💬 ${this.consoleIdentifier} Topic "${topic}" was subscribed.`);
		}
	}

	/**
	 * @private
	 * Unsubscribe topic
	 * ---
	 * Sends unsubscribe message
	 * @param {string} topic
	 * @param {*} reason
	 */
	_unsubscribeTopic(topic, reason = undefined) {
		if(this.sendMessage(E_GreenSocket_MessageTypes.UNSUBSCRIBE, reason, topic, {pushToQueueOnFail: false})) {
			logger.isTypeAllowed("info") && console.info(`💬 ${this.consoleIdentifier} Topic "${topic}" was unsubscribed.\nReason: ${reason}.`);
		}
	}

	/**
	 * Send message
	 * ---
	 * Sends a message or pushes it to the queue.
	 * @param {{
	 *   type: E_GreenSocket_MessageTypes,
	 *   data: *,
	 *   destination: string,
	 *   id: string,
	 * }} message
	 * @param {boolean} pushToQueueOnFail Whether to push the message to the queue if it fails to sent for any reason. (e.g. connection is not established or authorized)
	 */
	_sendMessage(message, pushToQueueOnFail = true) {
		const messageIdentifier = `${message.id ? '#' + message.id : `type ${message.type}`}`;

		if(this.isConnected ? this.isAuthorized || message?.type == E_GreenSocket_MessageTypes.LOGIN : false) {
			logger.isTypeAllowed("info") && console.info(`⬆️ ${this.consoleIdentifier} Sending message ${messageIdentifier}...`, message);

			this.socket.send(JSON.stringify(message));

			logger.isTypeAllowed("info") && console.info(`⬆️ ${this.consoleIdentifier} Message ${messageIdentifier} sent.`, message);

			return true;
		}
		else if(pushToQueueOnFail) {
			if(message.type == E_GreenSocket_MessageTypes.UNSUBSCRIBE) {
				const queuedSubscribeIndex = this.#queue.findIndex(item => item.type == E_GreenSocket_MessageTypes.SUBSCRIBE && item.destination == message.destination);

				if(queuedSubscribeIndex > -1) {
					logger.isTypeAllowed("info") && console.info(`💬 ${this.consoleIdentifier} Queued subscribe found for "${message.destination}" and will be removed.`);

					this.#queue.splice(queuedSubscribeIndex, 1);
				}
			}
			else if(message.type == E_GreenSocket_MessageTypes.UNSUBSCRIBE) {
				const queuedUnsubscribeIndex = this.#queue.findIndex(item => item.type == E_GreenSocket_MessageTypes.UNSUBSCRIBE && item.destination == message.destination);

				if(queuedUnsubscribeIndex > -1) {
					logger.isTypeAllowed("info") && console.info(`💬 ${this.consoleIdentifier} Queued unsubscribe found for "${message.destination}" and will be removed.`);

					this.#queue.splice(queuedUnsubscribeIndex, 1);
				}
			}

			this.#queue.push(message);

			logger.isTypeAllowed("warn") && console.warn(`⬆️ ${this.consoleIdentifier} Message ${messageIdentifier} could not be sent and has been added to the queue.`, message, {connected: this.isConnected, authorized: this.isAuthorized});
		}
		else {
			logger.isTypeAllowed("error") && console.error(`⬆️ ${this.consoleIdentifier} Message ${messageIdentifier} could not be sent.`, message, {connected: this.isConnected, authorized: this.isAuthorized});
		}

		return false;
	}

	/**
	 * @private
	 * Remove from subscriptions
	 * ---
	 * Internally removes a topic from subscriptions
	 * @param {string} topic
	 * @param {null|*} reason
	 * @return {boolean}
	 */
	_removeTopicFromSubscriptions(topic, reason) {
		const indexToRemove = this.#subscriptions.findIndex(subscription => subscription.topic == topic);

		if(indexToRemove > -1) {
			this.#subscriptions[indexToRemove].unsubscribeCallback(reason);
			this.#subscriptions.splice(indexToRemove, 1);
		}

		return indexToRemove > -1;
	}
}
